import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../themes/chartTheme';
import {
  Box,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { tokens } from '../theme';
import fetchData from '../helpers/fetchData';
import dayjs from 'dayjs';

interface IHistoricalRate {
  date: string;
  total_recipients: number;
  total_unique_opens: number;
  open_rate: number;
  total_ad_clicks: number;
  ad_click_rate: number;
  total_leads: number;
  lead_rate: number;
}

interface ISerie {
  id: string;
  data: { x: string; y: number }[];
  color?: string;
}

interface NewsletterGroupRatesChartProps {
  groupName: string;
  startDate: Date;
  endDate: Date;
  allowEspFilter?: boolean;
}

type RateType = 'open_rate' | 'ad_click_rate' | 'lead_rate';
type AggregationType = 'daily' | 'weekly' | 'monthly';

// Determine the appropriate aggregation based on date range
const determineAggregation = (startDate: Date, endDate: Date): AggregationType => {
  const diffInDays = dayjs(endDate).diff(dayjs(startDate), 'day');

  if (diffInDays > 120) {
    return 'monthly';
  } else if (diffInDays > 45) {
    return 'weekly';
  } else {
    return 'daily';
  }
};

// Format the date for display based on aggregation type
const formatDisplayDate = (dateString: string, aggregation: AggregationType): string => {
  switch (aggregation) {
    case 'monthly':
      // For YYYY-MM format
      const [year, month] = dateString.split('-');
      const monthNames = [
        'Jan',
        'Feb',
        'Mär',
        'Apr',
        'Mai',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dez',
      ];
      return `${monthNames[parseInt(month) - 1]} ${year}`;

    case 'weekly':
      // For YYYY-WW format
      const [weekYear, weekNum] = dateString.split('-');
      return `KW${weekNum} ${weekYear}`;

    case 'daily':
    default:
      // Keep YYYY-MM-DD format but make it more readable
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dayjs(dateString).format('DD.MM.YYYY');
      }
      return dateString;
  }
};

const rateLabels: Record<RateType, string> = {
  open_rate: 'Öffnungsrate',
  ad_click_rate: 'Ad-Klickrate',
  lead_rate: 'Leadrate',
};

const NewsletterGroupRatesChart: React.FC<NewsletterGroupRatesChartProps> = ({
  groupName,
  startDate,
  endDate,
  allowEspFilter = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRate, setSelectedRate] = useState<RateType>('open_rate');
  const [rawData, setRawData] = useState<IHistoricalRate[]>([]);
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType | null>(null);

  // Use selected aggregation if set, otherwise use auto-determined aggregation
  const aggregation = useMemo(() => {
    return selectedAggregation || determineAggregation(startDate, endDate);
  }, [selectedAggregation, startDate, endDate]);

  // Fetch data only when component mounts or when key props change
  useEffect(() => {
    let isMounted = true;

    const fetchChartData = async () => {
      if (!isMounted) return;

      setLoading(true);
      try {
        const options = {
          group_name: groupName,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          aggregation,
        };

        const data = (await fetchData<IHistoricalRate>(
          '/api/newsletter-group-historical-rates',
          options,
          true, // expectArray = true
        )) as IHistoricalRate[];

        if (isMounted) {
          setRawData(data);
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
        if (isMounted) {
          setRawData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchChartData();

    return () => {
      isMounted = false;
    };
  }, [groupName, startDate, endDate, aggregation]);

  // Memoize chart data to prevent unnecessary re-calculations
  const chartData = useMemo(() => {
    if (rawData.length > 0 && rawData[0].date) {
      const series: ISerie = {
        id: rateLabels[selectedRate],
        data: rawData.map((point) => ({
          x: formatDisplayDate(point.date, aggregation),
          y: Math.round(point[selectedRate] * 100) / 100, // Round to 2 decimal places
        })),
        color:
          selectedRate === 'open_rate'
            ? colors.greenAccent[600]
            : selectedRate === 'ad_click_rate'
              ? colors.blueAccent[600]
              : colors.redAccent[600],
      };

      return [series];
    }
    return [];
  }, [rawData, selectedRate, aggregation, colors]);

  const handleRateChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newRate: RateType | null) => {
      if (newRate !== null) {
        setSelectedRate(newRate);
      }
    },
    [],
  );

  const handleAggregationChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newAggregation: AggregationType | null) => {
      setSelectedAggregation(newAggregation);
    },
    [],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (chartData.length === 0 || chartData[0].data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography variant="body1" color="textSecondary">
          Keine Daten für den ausgewählten Zeitraum verfügbar
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={2}
      >
        <ToggleButtonGroup
          value={selectedRate}
          exclusive
          onChange={handleRateChange}
          aria-label="rate selection"
          size="small"
        >
          <ToggleButton value="open_rate" aria-label="open rate">
            Öffnungsrate
          </ToggleButton>
          <ToggleButton value="ad_click_rate" aria-label="ad click rate">
            Ad-Klickrate
          </ToggleButton>
          <ToggleButton value="lead_rate" aria-label="lead rate">
            Leadrate
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={aggregation}
          exclusive
          onChange={handleAggregationChange}
          aria-label="aggregation selection"
          size="small"
        >
          <ToggleButton value="daily" aria-label="daily aggregation">
            Täglich
          </ToggleButton>
          <ToggleButton value="weekly" aria-label="weekly aggregation">
            Wöchentlich
          </ToggleButton>
          <ToggleButton value="monthly" aria-label="monthly aggregation">
            Monatlich
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box height={400}>
        <ResponsiveLine
          data={chartData}
          theme={chartTheme}
          margin={{ top: 20, right: 110, bottom: 60, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            stacked: false,
            reverse: false,
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -30,
            legend: 'Datum',
            legendOffset: 50,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: `${rateLabels[selectedRate]} (%)`,
            legendOffset: -50,
            legendPosition: 'middle',
            format: (value) => `${value}%`,
          }}
          lineWidth={3}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            const point = slice.points[0];
            return (
              <Box
                sx={{
                  background: theme.palette.mode === 'dark' ? '#1e1e1e' : 'white',
                  color: theme.palette.mode === 'dark' ? 'white' : 'black',
                  padding: '9px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {String(point.data.x)}
                </Typography>
                <Typography variant="body2" style={{ color: point.serieColor }}>
                  {point.serieId}: {Number(point.data.y).toFixed(2)}%
                </Typography>
              </Box>
            );
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 100,
              translateY: 0,
              itemsSpacing: 0,
              itemDirection: 'left-to-right',
              itemWidth: 80,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default NewsletterGroupRatesChart;
